import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from "@mui/material";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {useTranslation} from "react-i18next";
import {FolioLineIssue, FolioLineIssues, hasBlockingIssues, isBlockingIssue} from "./folioLine.util";

const ISSUE_TEXT: Record<FolioLineIssue, string> = {
    NO_SERVICE: 'No service chosen',
    NO_PRICE: 'No price',
    NO_VAT: 'No VAT rate set',
};

interface Props {
    open: boolean
    /** The lines as the editor shows them - issues point into this list. */
    lines: FolioItem[]
    issues: FolioLineIssues[]
    onClose: () => void
    /** Only warnings are left: finalize anyway. */
    onContinue: () => void
}

/** Shown instead of the confirmation when Finalize can't charge the folio as it is. */
const FolioFinalizeIssues = ({open, lines, issues, onClose, onContinue}: Props) => {
    const {t} = useTranslation();
    const blocking = hasBlockingIssues(issues);
    const missingVat = issues.some(entry => entry.issues.includes('NO_VAT'));

    return (
        <Dialog open={open} onClose={onClose} maxWidth={'sm'} fullWidth>
            <DialogTitle>{blocking ? t('The folio is not ready yet') : t('Finalize without VAT?')}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {blocking ? t('Complete these lines before finalizing.') : t('These lines have no VAT rate.')}
                </DialogContentText>

                <List dense>
                    {issues.map(entry => {
                        const line = lines[entry.index];
                        const name = line?.description || t('New line');
                        const lineBlocked = entry.issues.some(isBlockingIssue);
                        return (
                            <ListItem key={entry.index} disableGutters>
                                <ListItemIcon sx={{minWidth: 36}}>
                                    {lineBlocked ? <ErrorOutlineIcon color={'error'}/> : <WarningAmberIcon color={'warning'}/>}
                                </ListItemIcon>
                                <ListItemText
                                    primary={`${t('Line {{number}}', {number: entry.index + 1})} · ${name}`}
                                    secondary={entry.issues.map(issue => t(ISSUE_TEXT[issue])).join(' · ')}
                                />
                            </ListItem>
                        );
                    })}
                </List>

                {missingVat && (
                    <DialogContentText variant={'body2'}>
                        {t('Casablanca books VAT exactly as transmitted and needs a VAT rate on every line. Set one, or choose "Charge 0% VAT" in the line\'s menu.')}
                    </DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant={blocking ? 'contained' : 'text'}>{t('Back to the folio')}</Button>
                {!blocking && <Button onClick={onContinue} variant={'contained'}>{t('Finalize anyway')}</Button>}
            </DialogActions>
        </Dialog>
    );
};

export default FolioFinalizeIssues;
